import type { Email, EmailAddress, EmailBodyPart } from '../api/types';
import { fullDate } from './format';
import { sanitiseOutgoing, textToHtml } from './html';

export type ComposeMode = 'new' | 'reply' | 'replyAll' | 'forward' | 'draft';

export interface DraftSeed {
    to: string;
    cc: string;
    subject: string;
    body: string;
    inReplyTo: string[] | null;
    references: string[] | null;
    /** Where the cursor should land — above the quoted text, as every client does. */
    caret: number;
}

const fmt = (a: EmailAddress) => (a.name ? `${a.name} <${a.email}>` : a.email);
const join = (list: EmailAddress[] | null | undefined) => (list ?? []).map(fmt).join(', ');

/** "Re: Re: Re: x" is noise; one prefix is the convention. */
function prefixOnce(subject: string, prefix: 'Re:' | 'Fwd:'): string {
    const clean = subject.replace(/^\s*((re|fwd|fw|ynt|ilt)\s*:\s*)+/i, '').trim();
    return `${prefix} ${clean}`.trim();
}

function plainBodyOf(email: Email): string {
    if (email.text?.trim()) return email.text;
    if (email.html?.trim()) {
        const raw = email.html;
        return raw
            .replace(/<(style|head|script)[^>]*>[\s\S]*?<\/\1>/gis, ' ')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n')
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/gi, ' ')
            .replace(/&amp;/gi, '&')
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>')
            .replace(/[ \t]+/g, ' ')
            .replace(/\n{3,}/g, '\n\n')
            .trim();
    }
    return '';
}

const quoted = (text: string) =>
    text
        .split('\n')
        .map((l) => (l.startsWith('>') ? `>${l}` : `> ${l}`))
        .join('\n');

export function buildDraft(mode: ComposeMode, source: Email | null, me: string, signature: string): DraftSeed {
    const sig = signature ? `\n\n-- \n${signature}` : '';

    if (mode === 'new' || !source) {
        return { to: '', cc: '', subject: '', body: sig, inReplyTo: null, references: null, caret: 0 };
    }

    // Resuming a saved draft: take it back exactly as it was, no quoting, no signature.
    if (mode === 'draft') {
        return {
            to: join(source.to),
            cc: join(source.cc),
            subject: source.subject ?? '',
            body: plainBodyOf(source),
            inReplyTo: source.inReplyTo,
            references: source.references,
            caret: 0,
        };
    }

    const from = source.from ?? [];
    const replyTargets = source.replyTo?.length ? source.replyTo : from;
    const mine = (a: EmailAddress) => a.email.toLowerCase() === me.toLowerCase();
    const attribution = `On ${fullDate(source.receivedAt)}, ${from[0]?.name || from[0]?.email || 'they'} wrote:`;
    const refs = [...(source.references ?? []), ...(source.messageId ?? [])];

    if (mode === 'forward') {
        const header = [
            '---------- Forwarded message ----------',
            `From: ${join(from)}`,
            `Date: ${fullDate(source.receivedAt)}`,
            `Subject: ${source.subject ?? ''}`,
            `To: ${join(source.to)}`,
        ].join('\n');
        const body = `${sig}\n\n${header}\n\n${plainBodyOf(source)}`;
        return {
            to: '',
            cc: '',
            subject: prefixOnce(source.subject ?? '', 'Fwd:'),
            body,
            inReplyTo: null,
            references: null,
            caret: 0,
        };
    }

    const to = mode === 'replyAll'
        ? [...replyTargets, ...(source.to ?? [])].filter((a) => !mine(a))
        : replyTargets;
    const cc = mode === 'replyAll' ? (source.cc ?? []).filter((a) => !mine(a)) : [];

    // De-duplicate: reply-all to a thread you're already on repeats addresses fast.
    const seen = new Set<string>();
    const uniq = (list: EmailAddress[]) =>
        list.filter((a) => {
            const k = a.email.toLowerCase();
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });

    const body = `${sig}\n\n${attribution}\n${quoted(plainBodyOf(source))}`;
    return {
        to: join(uniq(to)),
        cc: join(uniq(cc)),
        subject: prefixOnce(source.subject ?? '', 'Re:'),
        body,
        inReplyTo: source.messageId ?? null,
        references: refs.length ? refs : null,
        caret: 0,
    };
}

/* ---------------------------------------------------------------- HTML side */

const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * The original as HTML, ready to quote.
 *
 * `keepInline` decides what happens to `cid:` images. On a forward the original
 * parts travel with the message, so the references still resolve; on a reply
 * they don't, and a broken image icon in every recipient's client is worse than
 * the alt text.
 */
function htmlBodyOf(email: Email, keepInline: boolean): string {
    const raw = email.html?.trim()
        ? sanitiseOutgoing(email.html)
        : textToHtml(plainBodyOf(email));

    if (keepInline) return raw;

    const doc = new DOMParser().parseFromString(raw, 'text/html');
    for (const img of Array.from(doc.querySelectorAll('img'))) {
        if (!(img.getAttribute('src') ?? '').toLowerCase().startsWith('cid:')) continue;
        const alt = img.getAttribute('alt');
        img.replaceWith(doc.createTextNode(alt ? `[${alt}]` : ''));
    }
    return doc.body.innerHTML;
}

/**
 * The HTML counterpart of buildDraft.
 *
 * The two are built from the same source and kept structurally identical —
 * signature first, then the quote — so switching a half-written message between
 * plain and rich doesn't move the reply around under the cursor.
 */
export function buildHtmlDraft(mode: ComposeMode, source: Email | null, signature: string): string {
    const sig = signature ? `<p><br></p><p>--</p>${signature}` : '';
    const lead = '<p><br></p>';

    if (mode === 'new' || !source) return `${lead}${sig}`;
    if (mode === 'draft') return htmlBodyOf(source, true);

    const from = source.from ?? [];

    if (mode === 'forward') {
        const header = [
            ['From', join(from)],
            ['Date', fullDate(source.receivedAt)],
            ['Subject', source.subject ?? ''],
            ['To', join(source.to)],
        ]
            .map(([k, v]) => `<div><b>${k}:</b> ${esc(v ?? '')}</div>`)
            .join('');
        return `${lead}${sig}<p><br></p><div>---------- ${esc('Forwarded message')} ----------</div>${header}<p><br></p>${htmlBodyOf(source, true)}`;
    }

    const attribution = `On ${esc(fullDate(source.receivedAt))}, ${esc(from[0]?.name || from[0]?.email || 'they')} wrote:`;
    return `${lead}${sig}<p><br></p><div>${attribution}</div><blockquote style="margin: 0 0 0 8px; padding-left: 10px; border-left: 2px solid #c8c8d0">${htmlBodyOf(source, false)}</blockquote>`;
}

/**
 * The parts a forward, or a reopened draft, has to carry.
 *
 * Forwarding a message without its attachments is a bug the sender only finds out about from the
 * recipient, and reopening a draft used to quietly drop everything attached to it.
 *
 * IMAP has no reusable handle for a part — nothing a new message could simply point at — so
 * anything carried forward is fetched back down and attached again. This returns the list of what
 * to fetch; the composer does the fetching.
 */
export function attachmentsToCarry(mode: ComposeMode, source: Email | null): EmailBodyPart[] {
    if (!source || (mode !== 'forward' && mode !== 'draft')) return [];
    return (source.attachments ?? []).filter((a) => a.partId);
}

const ADDRESS = /^[^\s@,<>]+@[^\s@,<>]+\.[^\s@,<>]+$/;

/** Parses a comma-separated recipient field into JMAP addresses, flagging bad ones. */
export function parseAddresses(input: string): { valid: EmailAddress[]; invalid: string[] } {
    const valid: EmailAddress[] = [];
    const invalid: string[] = [];
    for (const raw of input.split(',').map((s) => s.trim()).filter(Boolean)) {
        const angled = /^(.*?)<([^>]+)>$/.exec(raw);
        const name = angled ? angled[1]?.trim().replace(/^"|"$/g, '') || null : null;
        const address = (angled ? angled[2] : raw)?.trim() ?? '';
        if (ADDRESS.test(address)) valid.push({ name: name || null, email: address });
        else invalid.push(raw);
    }
    return { valid, invalid };
}
